/**
 * Chip/card palette for `AutomationLeaseState`, extracted out of
 * `LeaseStatusSection.tsx` so it can be reused wherever a state chip is
 * needed without a second copy — `SlotsSections.tsx` (A7Y-3600) maps
 * `SlotState` onto this same palette. `react-refresh/only-export-components`
 * refuses a non-component export from a file that also exports a component,
 * which is why this lives in its own pure `.ts` file rather than being
 * exported straight out of `LeaseStatusSection.tsx`.
 */
import type { AutomationLeaseState } from '@a7y/types';

/**
 * Each state's presentation. `null` is deliberately not in this table — an
 * unknown state is not a state, and it renders through its own branch so it can
 * never be styled as one of the healthy ones.
 */
export const STATE_META: Record<
  AutomationLeaseState,
  { label: string; chip: string; card: string }
> = {
  free: {
    label: 'Free',
    chip: 'bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400',
    card: 'border-zinc-200 dark:border-zinc-800',
  },
  reserved: {
    label: 'Reserved',
    chip: 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300',
    card: 'border-blue-400 dark:border-blue-600',
  },
  active: {
    label: 'Active',
    chip: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300',
    card: 'border-emerald-500 dark:border-emerald-500',
  },
  handoff_pending: {
    label: 'Handoff pending',
    chip: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300',
    card: 'border-amber-400 dark:border-amber-600',
  },
  quarantined: {
    label: 'Quarantined',
    chip: 'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300',
    card: 'border-red-400 dark:border-red-600',
  },
};

export const UNKNOWN_META = {
  label: 'Unknown',
  chip: 'bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400',
  card: 'border-dashed border-zinc-300 dark:border-zinc-700',
};
